import { useEffect, useState, useRef, useCallback } from 'react';
import { useFasting } from '@/contexts/FastingContext';
import {
  isNativePlatform,
  checkNotificationPermission,
  scheduleLocalNotification,
  cancelAllNotifications,
  showNotification,
} from '@/services/localNotificationService';
import { supabase } from '@/integrations/supabase/client';

interface FastingNotificationData {
  id: string;
  trigger_type: 'milestone' | 'before_end' | 'planned_start' | 'scheduled_fast';
  trigger_minutes: number;
  timing: 'before' | 'after';
  message: string;
  is_enabled: boolean;
}

/**
 * Milestones, before-end, and scheduled-fast notifications are now handled
 * server-side by the send-fasting-notifications edge function (cron every 2 min).
 *
 * This component only handles:
 * - Cancelling stale local notifications when a fast stops
 * - Planned-start reminders (ephemeral client state, not persisted to DB)
 */
export function FastingNotificationManager() {
  const {
    isActive,
    plannedStartTime,
    notificationSettings,
    markNotificationSent,
  } = useFasting();

  const [permissionGranted, setPermissionGranted] = useState(false);
  const [notifications, setNotifications] = useState<FastingNotificationData[]>([]);
  const notificationsLoadedRef = useRef(false);
  const scheduledNativeIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const loadNotifications = async () => {
      if (notificationsLoadedRef.current) return;
      const { data, error } = await supabase
        .from('fasting_notifications')
        .select('*')
        .eq('is_enabled', true)
        .eq('trigger_type', 'planned_start')
        .order('sort_order', { ascending: true });

      if (!error && data) {
        setNotifications(data as FastingNotificationData[]);
        notificationsLoadedRef.current = true;
      }
    };
    loadNotifications();
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        const has = await checkNotificationPermission();
        setPermissionGranted(has);
      } catch (e) {
        console.error('Error checking notification permission:', e);
      }
    };
    init();
  }, []);

  const scheduleAtTime = useCallback(async (key: string, message: string, fireAt: Date) => {
    if (scheduledNativeIdsRef.current.has(key)) return;
    if (fireAt.getTime() <= Date.now()) return;
    scheduledNativeIdsRef.current.add(key);
    await scheduleLocalNotification('עדכון צום', message, fireAt);
  }, []);

  const sendImmediate = useCallback(async (message: string, key: string) => {
    const lastSent = notificationSettings.lastNotificationSent[key];
    if (lastSent && Date.now() - lastSent < 5 * 60 * 1000) return;
    if (!permissionGranted) { markNotificationSent(key); return; }

    if (isNativePlatform() || document.hidden) {
      await showNotification('עדכון צום', message);
    }
    markNotificationSent(key);
  }, [permissionGranted, notificationSettings.lastNotificationSent, markNotificationSent]);

  // Cancel pending OS-level local notifications when fast stops
  useEffect(() => {
    if (!isActive) {
      scheduledNativeIdsRef.current.clear();
      cancelAllNotifications();
    }
  }, [isActive]);

  // ─── Planned start time notifications (client-side only) ───
  useEffect(() => {
    if (!plannedStartTime || !notificationSettings.enablePlannedStart || notifications.length === 0) return;

    const plannedStartNotifications = notifications.filter(n => n.trigger_type === 'planned_start');
    if (plannedStartNotifications.length === 0) return;

    if (isNativePlatform() && permissionGranted) {
      const [hours, minutes] = plannedStartTime.split(':').map(Number);
      const planned = new Date();
      planned.setHours(hours, minutes, 0, 0);
      if (planned.getTime() < Date.now()) planned.setDate(planned.getDate() + 1);

      plannedStartNotifications.forEach(n => {
        const fireAt = new Date(planned.getTime() - n.trigger_minutes * 60 * 1000);
        scheduleAtTime(`planned_start_${n.trigger_minutes}`, n.message, fireAt);
      });
      return;
    }

    const checkPlannedStart = () => {
      const now = new Date();
      const [hours, minutes] = plannedStartTime.split(':').map(Number);
      const planned = new Date();
      planned.setHours(hours, minutes, 0, 0);
      if (planned.getTime() < now.getTime()) planned.setDate(planned.getDate() + 1);

      const diffMinutes = Math.floor((planned.getTime() - now.getTime()) / 60000);
      plannedStartNotifications.forEach(n => {
        if (Math.abs(diffMinutes - n.trigger_minutes) < 1) {
          sendImmediate(n.message, `planned_start_${n.trigger_minutes}`);
        }
      });
    };

    checkPlannedStart();
    const interval = setInterval(checkPlannedStart, 60000);
    return () => clearInterval(interval);
  }, [plannedStartTime, notificationSettings.enablePlannedStart, notifications, permissionGranted, scheduleAtTime, sendImmediate]);

  return null;
}
